import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../../services/HttpManager";
import useEsTelefono from "../../../../utils/esTelefono";
import { DIAS_PLURAL, TIEMPOS, aHHMM, hora12, mensajeServidor, minutos } from "./horarioFormApi";
import { textoConflicto } from "./horarioFormAviso";
import { fechaDMY } from "./formato";
import { PlanCambioHora, SesionHorario } from "./interfaces";
import S from "./strings";
import "./horarioForm.css";
import "./horarios.css";

/*
	Cambiar Hora (Horarios Claros, fase 2). Una sola ventana para los dos caminos:
	  - «Usar Hora del Nombre» (editable = false): las sesiones ya vienen; solo se ve Qué Va a Pasar y se confirma.
	  - «Cambiar Hora» desde Editar Horario (editable = true): se eligen día y horas y Qué Va a Pasar se recalcula al momento.
	Qué Va a Pasar y el cambio real los calcula el mismo código en el servidor. Si choca, no deja confirmar (salvo Super Admin con motivo).
*/
const http = HttpManager.getInstance();

interface Props {
	campusId: number;
	horarioId: number;
	titulo: string;
	sesionesIniciales: SesionHorario[];
	editable: boolean;
	/** Motivo que se anota en el historial cuando no se pide (editable = false). */
	motivoAuto?: string;
	onClose: () => void;
	/** El cambio se guardó. */
	onHecho: () => void;
}

const letras = (t: string): number => t.replace(/\s+/g, "").length;
const sesionValida = (s: SesionHorario): boolean => minutos(s.fin) > minutos(s.inicio);

const CambiarHora: React.FC<Props> = ({ campusId, horarioId, titulo, sesionesIniciales, editable, motivoAuto, onClose, onHecho }) => {
	const movil = useEsTelefono();
	const [sesiones, setSesiones] = useState<SesionHorario[]>(sesionesIniciales);
	const [plan, setPlan] = useState<PlanCambioHora | null>(null);
	const [calculando, setCalculando] = useState<boolean>(false);
	const [errorPlan, setErrorPlan] = useState<string | null>(null);
	const [motivo, setMotivo] = useState<string>("");
	const [motivoChoque, setMotivoChoque] = useState<string>("");
	const [guardando, setGuardando] = useState<boolean>(false);
	const [errorGuardar, setErrorGuardar] = useState<string | null>(null);
	const [verClases, setVerClases] = useState<boolean>(false);
	const pedido = useRef<number>(0);

	// Qué Va a Pasar se recalcula (con una pausa corta) cada vez que cambian las horas.
	useEffect(() => {
		if (!sesiones.length || !sesiones.every(sesionValida)) { setPlan(null); setCalculando(false); return; }
		// Sin tocar nada no hay nada que calcular (el servidor diría «Esa ya es la hora del horario»).
		if (editable && JSON.stringify(sesiones) === JSON.stringify(sesionesIniciales)) { pedido.current++; setPlan(null); setErrorPlan(null); setCalculando(false); return; }
		const n = ++pedido.current;
		setCalculando(true); setErrorPlan(null);
		const t = window.setTimeout(() => {
			http.horariosService.vistaPreviaCambioHora(campusId, horarioId, sesiones)
				.then((r) => { if (n === pedido.current) { setPlan(r.data); setCalculando(false); } })
				.catch((e) => { if (n === pedido.current) { setPlan(null); setErrorPlan(mensajeServidor(e).texto || S.ch.error); setCalculando(false); } });
		}, editable ? 350 : 0);
		return () => window.clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, horarioId, sesiones, editable]);

	const cambiarSesion = (i: number, p: Partial<SesionHorario>) => setSesiones((l) => l.map((s, k) => (k === i ? { ...s, ...p } : s)));
	const cambiarEntrada = (i: number, v: string) => setSesiones((l) => l.map((s, k) => {
		if (k !== i) return s;
		return { ...s, inicio: v, fin: minutos(s.fin) <= minutos(v) ? aHHMM(Math.min(minutos(v) + 180, 1320)) : s.fin };
	}));

	const hayChoque = !!plan && plan.conflictos.length > 0;
	const motivoOk = !editable || letras(motivo) >= 5;
	const choqueOk = !hayChoque || (!!plan && plan.puedeForzar && letras(motivoChoque) >= 10);
	const puedeConfirmar = !!plan && !calculando && !guardando && plan.errores.length === 0 && motivoOk && choqueOk;

	const confirmar = async () => {
		if (!plan || !puedeConfirmar) return;
		setGuardando(true); setErrorGuardar(null);
		try {
			await http.horariosService.cambiarHora(campusId, horarioId, {
				sesiones, motivo: editable ? motivo.trim() : (motivoAuto || "Hora del nombre del horario"),
				...(hayChoque ? { forzarChoque: { motivo: motivoChoque.trim() } } : {})
			});
			onHecho();
		} catch (e: any) {
			setErrorGuardar(mensajeServidor(e).texto || S.ch.errorGuardar);
			setGuardando(false);
		}
	};

	const dist = (nuevo: boolean) => (nuevo ? plan?.horario.horaNueva : plan?.horario.hora) || "";
	const r = plan?.resumen;

	return (
		<Modal className="waModulo hcVentana hcCambio" show onHide={guardando ? undefined : onClose} size="lg" centered scrollable fullscreen="sm-down">
			<Modal.Header closeButton={!guardando}>
				<Modal.Title style={{ fontSize: movil ? 15 : 18 }}>
					<div className="hcTituloVentana"><span>{S.ch.titulo}</span><small>{titulo}</small></div>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ textAlign: "left", fontSize: 15, paddingLeft: movil ? 14 : 22, paddingRight: movil ? 14 : 22 }}>
				{editable ? (
					<div className="hcChEditor">
						{sesiones.map((s, i) => (
							<div className="hcChFila" key={i}>
								<div className="hf-campo"><label>{S.ch.dia}</label>
									<select className="hf-in" value={s.dia} onChange={(e) => cambiarSesion(i, { dia: Number(e.target.value) })}>
										{DIAS_PLURAL.map((d, k) => <option key={d} value={k} disabled={sesiones.some((x, j) => j !== i && x.dia === k)}>{d}</option>)}
									</select>
								</div>
								<div className="hf-campo"><label>{S.ch.entrada}</label>
									<select className="hf-in" value={s.inicio} onChange={(e) => cambiarEntrada(i, e.target.value)}>
										{TIEMPOS.slice(0, -1).map((t) => <option key={t} value={t}>{hora12(t)}</option>)}
									</select>
								</div>
								<div className="hf-campo"><label>{S.ch.salida}</label>
									<select className="hf-in" value={s.fin} onChange={(e) => cambiarSesion(i, { fin: e.target.value })}>
										{TIEMPOS.slice(1).map((t) => <option key={t} value={t}>{hora12(t)}</option>)}
									</select>
								</div>
							</div>
						))}
						<div className="hf-campo" style={{ marginTop: 10 }}><label>{S.ch.motivo}</label>
							<input className="hf-in" value={motivo} maxLength={300} onChange={(e) => setMotivo(e.target.value)} />
						</div>
					</div>
				) : null}

				{errorPlan ? <div className="hcAviso">{errorPlan}</div> : null}
				{!plan && !errorPlan && (calculando || !editable) ? <div className="loader" aria-label={S.ch.cargando} /> : null}

				{plan ? (<div style={{ opacity: calculando ? 0.55 : 1 }}>
					<div className="hcDatos">
						<div className="hcDato"><small>{S.ch.antes}</small><div>{dist(false)}</div></div>
						<div className="hcDato"><small>{S.ch.despues}</small><div>{dist(true)}</div></div>
						<div className="hcDato"><small>{S.ch.nombreNuevo}</small><div>{plan.horario.nombreNuevo}</div></div>
						<div className="hcDato"><small>{S.ch.fin}</small><div>{fechaDMY(plan.horario.fin)}{plan.horario.fin !== plan.horario.finNuevo ? ` → ${fechaDMY(plan.horario.finNuevo)}` : ""}</div></div>
					</div>

					{plan.errores.length > 0 ? <div className="hcAviso" style={{ marginTop: 12 }}>{plan.errores.map((e, i) => <div key={i}>{e}</div>)}</div> : (<>
						<div className="hcChips" style={{ marginTop: 12 }}>
							<span className="hcChip acento">{S.ch.seMueven(r!.movidas)}</span>
							{r!.seQuedan > 0 ? <span className="hcChip oro">{S.ch.seQuedan(r!.seQuedan)}</span> : null}
							{r!.pasadas > 0 ? <span className="hcChip">{S.ch.yaPasaron(r!.pasadas)}</span> : null}
						</div>
						{r!.movidas + r!.seQuedan > 0 ? (
							<button type="button" className="btn btn-outline-secondary btn-sm hcChVer" onClick={() => setVerClases((v) => !v)}>{verClases ? S.ch.ocultarClases : S.ch.verClases}</button>
						) : null}
						{verClases ? (
							<div className="hcScroll hcChClases">
								{plan.movidas.length > 0 ? <table className="table table-sm hcTabla">
									<thead><tr><th>{S.ch.colClase}</th><th>{S.ch.colAntes}</th><th>{S.ch.colDespues}</th></tr></thead>
									<tbody>{plan.movidas.map((m) => (
										<tr key={m.claseId}><td>{m.numero}</td><td>{fechaDMY(m.de.fecha)} {hora12(m.de.inicio)}</td><td>{fechaDMY(m.a.fecha)} {hora12(m.a.inicio)}</td></tr>
									))}</tbody>
								</table> : null}
								{plan.seQuedan.length > 0 ? <table className="table table-sm hcTabla">
									<thead><tr><th>{S.ch.colClase}</th><th>{S.ch.colFecha}</th><th>{S.ch.colMotivo}</th></tr></thead>
									<tbody>{plan.seQuedan.map((m) => (
										<tr key={m.claseId}><td>{m.numero}</td><td>{fechaDMY(m.fecha)} {hora12(m.inicio)}</td><td className="hcOroTexto">{m.motivo}</td></tr>
									))}</tbody>
								</table> : null}
							</div>
						) : null}
					</>)}

					{hayChoque ? (
						<div className="hcAviso" role="alert" style={{ marginTop: 12 }}>
							{plan.conflictos.map((c, i) => <div key={i}>{textoConflicto(c)}</div>)}
							{plan.puedeForzar ? (
								<div className="hf-campo" style={{ marginTop: 8 }}><label>{S.ch.motivoChoque}</label>
									<input className="hf-in" value={motivoChoque} maxLength={300} onChange={(e) => setMotivoChoque(e.target.value)} />
								</div>
							) : null}
						</div>
					) : null}
				</div>) : null}

				{errorGuardar ? <div className="hcAviso" style={{ marginTop: 12 }}>{errorGuardar}</div> : null}
			</Modal.Body>
			<Modal.Footer className="hcPie">
				<button type="button" className="btn btn-outline-secondary btn-sm" disabled={guardando} onClick={onClose}>{S.cancelar}</button>
				<button type="button" className="btn btn-primary btn-sm" disabled={!puedeConfirmar} onClick={confirmar}>{hayChoque ? S.ch.confirmarChoque : S.ch.confirmar}</button>
			</Modal.Footer>
		</Modal>
	);
};

export default CambiarHora;
